
import React from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Search, FileText } from "lucide-react";
import { TemplateCategory, Template } from "@/data/templates";

interface CategoriesViewProps {
  categories: TemplateCategory[];
  globalSearchTerm: string;
  setGlobalSearchTerm: (term: string) => void;
  onSelectCategory: (categoryId: string) => void;
  onSelectTemplate: (template: Template) => void;
}

const CategoriesView = ({ 
  categories, 
  globalSearchTerm, 
  setGlobalSearchTerm, 
  onSelectCategory,
  onSelectTemplate 
}: CategoriesViewProps) => {
  // Filtrar categorias baseado na busca global
  const filterCategoriesGlobally = () => {
    if (!globalSearchTerm) return categories;
    
    return categories.map(category => ({
      ...category,
      subcategories: category.subcategories.map(subcategory => ({
        ...subcategory,
        templates: subcategory.templates.filter(template =>
          template.name.toLowerCase().includes(globalSearchTerm.toLowerCase()) ||
          template.description.toLowerCase().includes(globalSearchTerm.toLowerCase()) ||
          template.type.toLowerCase().includes(globalSearchTerm.toLowerCase()) ||
          category.name.toLowerCase().includes(globalSearchTerm.toLowerCase()) ||
          subcategory.name.toLowerCase().includes(globalSearchTerm.toLowerCase())
        )
      })).filter(subcategory => subcategory.templates.length > 0)
    })).filter(category => category.subcategories.length > 0);
  };

  const filteredCategories = filterCategoriesGlobally();

  return (
    <div className="space-y-4">
      <div className="text-center mb-6">
        <h2 className="text-2xl font-bold mb-2 text-indigo-900">+ de 220 Modelos de documentos</h2>
        <p className="mb-4 text-purple-800 font-medium">
          Escolha uma categoria para ver os modelos disponíveis
        </p>
        
        {/* Campo de busca geral */}
        <div className="relative max-w-md mx-auto">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
          <Input
            placeholder="Buscar em todos os modelos..."
            value={globalSearchTerm}
            onChange={(e) => setGlobalSearchTerm(e.target.value)}
            className="pl-10 bg-white/10 border-white/30 text-indigo-900 placeholder:text-indigo-700 focus:border-white/60"
          />
        </div>
      </div>

      {globalSearchTerm && filteredCategories.length === 0 && (
        <div className="text-center py-8 text-purple-800 font-medium">
          <FileText className="h-12 w-12 mx-auto mb-4 opacity-50" />
          <p>Nenhum modelo encontrado para "{globalSearchTerm}"</p>
        </div>
      )}

      {globalSearchTerm && filteredCategories.length > 0 ? (
        // Mostrar resultados da busca global
        <div className="space-y-6 overflow-y-auto max-h-[calc(100vh-180px)] px-4 pb-6">
          {filteredCategories.map((category) => (
            <div key={category.id}>
              <h3 className="text-lg font-semibold mb-4 text-indigo-800 flex items-center gap-2">
                <span className="text-xl">{category.emoji}</span>
                {category.name}
              </h3>
              {category.subcategories.map((subcategory) => (
                <div key={subcategory.id} className="mb-4">
                  <h4 className="text-md font-medium mb-2 text-purple-700 flex items-center gap-2 ml-4">
                    <span className="text-lg">{subcategory.emoji}</span>
                    {subcategory.name}
                  </h4>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 ml-8">
                    {subcategory.templates.map((template) => (
                      <Card 
                        key={template.id}
                        className="document-template-card cursor-pointer"
                        onClick={() => onSelectTemplate(template)}
                      >
                        <CardHeader>
                          <CardTitle className="text-base flex items-center gap-2 text-indigo-900">
                            <FileText className="h-4 w-4 text-blue-600" />
                            {template.name}
                          </CardTitle>
                        </CardHeader>
                        <CardContent>
                          <p className="text-sm mb-3 text-gray-600">
                            {template.description}
                          </p>
                          <Badge variant="secondary" className="text-xs bg-blue-100 text-blue-800">
                            {template.type}
                          </Badge>
                        </CardContent>
                      </Card>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          ))}
        </div>
      ) : !globalSearchTerm ? (
        // Mostrar categorias normais quando não há busca
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {categories.map((category) => {
            const templateCount = category.subcategories.reduce((total, sub) => total + sub.templates.length, 0);
            
            return (
              <Card 
                key={category.id}
                className="document-template-card cursor-pointer"
                onClick={() => onSelectCategory(category.id)}
              >
                <CardHeader>
                  <CardTitle className="text-lg flex items-center gap-2 text-indigo-900">
                    <span className="text-2xl">{category.emoji}</span>
                    {category.name}
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-sm mb-3 text-gray-600">
                    {category.description}
                  </p>
                  <Badge variant="outline" className="border-blue-200 text-blue-700">
                    {templateCount} modelos
                  </Badge>
                </CardContent>
              </Card>
            );
          })}
        </div>
      ) : null}
    </div>
  );
};

export default CategoriesView;
